import { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";

import MenuItem from "../MenuItem/MenuItem";
import {
  admin_client_setup,
  admin_logs_and_history,
  admin_manage_user,
} from "./AdminMenuItems";
import {
  client_logs_and_history,
  client_access_management,
  lockBox,
  route_management,
  systemSettings,
} from "./ClientMenuItems";
import "./Sidebar.css";
import {
  sub_client_client_setup,
  sub_client_manage_user,
  sub_client_account_management,
  sub_client_billing,
  sub_client_reports,
  sub_client_system_settings,
  sub_client_logs_and_history,
  sub_client_import_export,
} from "./SubClientMenuItems";
import {
  transfer_station_sub_client_billing,
  transfer_station_sub_client_account_management,
  transfer_station_sub_client_logs_and_history,
  transfer_station_sub_client_system_settings,
  transfer_station_sub_client_reports,
  transfer_station_weigh_master,
} from "./TransferStationSubclientMenuItems";

import { useAuth } from "context/AuthContext";
import { useSidebarToggle } from "context/ToggleSideBarContext";
import Icon, { IconName } from "components/Icon";
import { useRateMaintenanceMenu } from "./useRateMaintenanceMenu";
import { selectMenu } from "./menuHelpers";

import Logo from "../Header/Logo";
import { useReloadSystemCache } from "../../../hooks/useReloadSystemCache";
import AppLoader from "../AppLoader";

type SideBarProps = {
  clientPermissions: string[];
  permissions: string[];
  subClientPermissions: string[];
};

const ROUTE_MENU_PATTERNS: Record<string, RegExp[]> = {
  "Client Setup": [
    /^\/admin\/client-setup/,
    /^\/client\/client-setup/,
    /^\/subclient\/client-setup/,
    /^\/admin\/subclient-setup/,
    /^\/admin\/schedular-details/,
  ],
  "Manage User": [/^\/admin\/manage-user/],
  "Access Management": [/^\/client\/access-management/],
  "Logs and History": [
    /^\/admin\/log-history/,
    /^\/client\/log-history/,
    /^\/subclient\/log-history/,
  ],
  "System Settings": [
    /^\/client\/system-settings/,
    /^\/subclient\/system-settings/,
  ],
  "Route Management": [/^\/client\/route-management/],
  Lockbox: [/^\/client\/lockbox/],
  Reports: [/^\/subclient\/reports/],
  "Rate Management": [/^\/subclient\/rate-management/],
  "Account Management": [/^\/subclient\/account-management/],
  Billing: [/^\/subclient\/billing/],
};

const Dashboard = ({ to }: { to: string }) => (
  <li className="menu-item">
    <NavLink to={to} className="nav-link">
      <span className="accordion__title__container">
        <span className="accordion__title">
          <Icon name="dashboard" size={20} tabIndex={-1} />
          <span className="title">Dashboard</span>
        </span>
      </span>
    </NavLink>
  </li>
);

/* ------------------------------------------------------------------ */
/*  MenuConfig — every branch carries navTitle (used for React key)   */
/* ------------------------------------------------------------------ */

type MenuConfig =
  | {
    kind: "dashboard";
    navTitle: string;
    to: string;
  }
  | {
    kind: "accordion";
    navTitle: string;
    iconClassName: IconName;
    iconSize?: number;
    permissionsToMatch?: any;
    basePermission?: string;
  }
  | {
    kind: "link";
    navTitle: string;
    iconClassName: IconName;
    iconSize?: number;
    permissionsToMatch?: any;
    navigateTo: string;
  }
  | {
    kind: "button";
    navTitle: string;
    iconClassName: IconName;
    iconSize?: number;
    onClick: () => void;
  };

const renderMenuItem = (
  config: MenuConfig,
  openMenu: string | null,
  onToggle: (title: string | null) => void,
  rolePermissions: string[],
) => {
  const key = config.navTitle;

  if (config.kind === "dashboard") {
    return <Dashboard key={key} to={config.to} />;
  }

  if (config.kind === "button") {
    return (
      <MenuItem
        key={key}
        as="button"
        iconClassName={config.iconClassName}
        navTitle={config.navTitle}
        iconSize={config.iconSize}
        onClick={config.onClick}
      />
    );
  }

  const isOpen = openMenu === config.navTitle;

  if (config.kind === "link") {
    return (
      <MenuItem
        key={key}
        permissions={rolePermissions}
        iconClassName={config.iconClassName}
        navTitle={config.navTitle}
        iconSize={config.iconSize}
        permissionsToMatch={config.permissionsToMatch}
        navigateTo={config.navigateTo}
        onToggle={() => onToggle(null)}
      />
    );
  }

  return (
    <MenuItem
      key={key}
      permissions={rolePermissions}
      iconClassName={config.iconClassName}
      navTitle={config.navTitle}
      iconSize={config.iconSize}
      permissionsToMatch={config.permissionsToMatch}
      basePermission={config.basePermission}
      isOpen={isOpen}
      onToggle={() => onToggle(config.navTitle)}
    />
  );
};

export default function Sidebar({
  clientPermissions,
  permissions,
  subClientPermissions,
}: SideBarProps) {
  const {
    user,
    userRole,
    isTransferStationBusinessTypeEnabled,
    isWaterMeterBusinessTypeEnabled,
  } = useAuth();

  const { isLoading: isReloadSystemCacheLoading, mutate: reloadSystemCache } =
    useReloadSystemCache();

  const sidebarCtx = useSidebarToggle();
  const isSideBarOpen = (sidebarCtx as any)?.isSideBarOpen ?? false;

  const handleCloseSidebar = useCallback(() => {
    const c = sidebarCtx as any;
    if (!c) return;

    if (typeof c.toggleSideBar === "function") c.toggleSideBar();
    else if (typeof c.toggleSidebar === "function") c.toggleSidebar();
    else if (typeof c.handleSidebarToggle === "function")
      c.handleSidebarToggle();
    else if (typeof c.setIsSideBarOpen === "function") c.setIsSideBarOpen(false);
    else if (typeof c.setIsSidebarOpen === "function")
      c.setIsSidebarOpen(false);
  }, [sidebarCtx]);

  const [openMenu, setOpenMenu] = useState<string | null>(null);

  const handleMenuToggle = useCallback((menuTitle: string | null) => {
    setOpenMenu((prev) => (prev === menuTitle ? null : menuTitle));
  }, []);

  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    for (const [menuTitle, patterns] of Object.entries(ROUTE_MENU_PATTERNS)) {
      if (patterns.some((p) => p.test(path))) {
        setOpenMenu(menuTitle);
        return;
      }
    }
  }, [location.pathname]);

  const { filteredRateManagement, isVisible: isRateManagementVisible } =
    useRateMaintenanceMenu(
      subClientPermissions,
      isTransferStationBusinessTypeEnabled,
    );

  const adminMenus = useMemo<MenuConfig[]>(() => {
    const menus: MenuConfig[] = [
      {
        kind: "dashboard",
        navTitle: "Dashboard",
        to: "/admin/dashboard",
      },
      {
        kind: "accordion",
        navTitle: "Client Setup",
        iconClassName: "clientSetup",
        permissionsToMatch: admin_client_setup,
      },
      {
        kind: "accordion",
        navTitle: "Manage User",
        iconClassName: "users",
        permissionsToMatch: admin_manage_user,
      },
      {
        kind: "accordion",
        navTitle: "Logs and History",
        iconClassName: "history",
        permissionsToMatch: admin_logs_and_history,
      },
    ];

    if (user?.isUpdateSystemCacheAllowed) {
      menus.push({
        kind: "button",
        navTitle: "Reload System Cache",
        iconClassName: "reload",
        iconSize: 18,
        onClick: () => reloadSystemCache(),
      });
    }

    return menus;
  }, [user?.isUpdateSystemCacheAllowed, reloadSystemCache]);

  const clientMenus = useMemo<MenuConfig[]>(
    () => [
      {
        kind: "dashboard",
        navTitle: "Dashboard",
        to: "/client/dashboard",
      },
      {
        kind: "accordion",
        navTitle: "Access Management",
        iconClassName: "users",
        permissionsToMatch: client_access_management,
      },
      {
        kind: "accordion",
        navTitle: "Route Management",
        iconClassName: "route",
        permissionsToMatch: route_management,
      },
      {
        kind: "accordion",
        navTitle: "Lockbox",
        iconClassName: "moneyCheckDollar",
        permissionsToMatch: lockBox,
      },
      {
        kind: "accordion",
        navTitle: "System Settings",
        iconClassName: "gear",
        permissionsToMatch: systemSettings,
        iconSize: 18,
      },
      {
        kind: "accordion",
        navTitle: "Logs and History",
        iconClassName: "history",
        permissionsToMatch: client_logs_and_history,
      },
    ],
    [],
  );

  const subClientMenus = useMemo<MenuConfig[]>(() => {
    const menus: MenuConfig[] = [
      {
        kind: "accordion",
        navTitle: "Client Setup",
        iconClassName: "clientSetup",
        permissionsToMatch: sub_client_client_setup,
      },
      {
        kind: "accordion",
        navTitle: "Manage User",
        iconClassName: "users",
        permissionsToMatch: sub_client_manage_user,
      },
      {
        kind: "accordion",
        navTitle: "Account Management",
        iconClassName: "customers",
        permissionsToMatch: selectMenu(
          isTransferStationBusinessTypeEnabled,
          transfer_station_sub_client_account_management,
          sub_client_account_management,
        ),
      },
    ];

    if (isTransferStationBusinessTypeEnabled) {
      menus.push({
        kind: "link",
        navTitle: "WeighMaster",
        iconClassName: "truck",
        permissionsToMatch: transfer_station_weigh_master,
        navigateTo: "/subclient/weigh-master",
      });
    }

    if (isRateManagementVisible) {
      menus.push({
        kind: "accordion",
        navTitle: "Rate Management",
        iconClassName: "dollarCircle",
        permissionsToMatch: filteredRateManagement,
        iconSize: 22,
      });
    }

    menus.push(
      {
        kind: "accordion",
        navTitle: "Billing",
        iconClassName: "receipt",
        permissionsToMatch: selectMenu(
          isTransferStationBusinessTypeEnabled,
          transfer_station_sub_client_billing,
          sub_client_billing,
        ),
        iconSize: 18,
      },
      {
        kind: "accordion",
        navTitle: "Reports",
        iconClassName: "solidReport",
        permissionsToMatch: selectMenu(
          isTransferStationBusinessTypeEnabled,
          transfer_station_sub_client_reports,
          sub_client_reports,
        ),
      },
      {
        kind: "accordion",
        navTitle: "System Settings",
        iconClassName: "gear",
        permissionsToMatch: selectMenu(
          isTransferStationBusinessTypeEnabled,
          transfer_station_sub_client_system_settings,
          sub_client_system_settings,
        ),
        iconSize: 18,
      },
      {
        kind: "accordion",
        navTitle: "Logs and History",
        iconClassName: "history",
        permissionsToMatch: selectMenu(
          isTransferStationBusinessTypeEnabled,
          transfer_station_sub_client_logs_and_history,
          sub_client_logs_and_history,
        ),
      },
    );

    if (!isTransferStationBusinessTypeEnabled && isWaterMeterBusinessTypeEnabled) {
      menus.push({
        kind: "link",
        navTitle: "Import/Export",
        iconClassName: "importOrExport",
        permissionsToMatch: sub_client_import_export,
        navigateTo: "/subclient/import-export",
        iconSize: 24,
      });
    }

    return menus;
  }, [
    isTransferStationBusinessTypeEnabled,
    isWaterMeterBusinessTypeEnabled,
    isRateManagementVisible,
    filteredRateManagement,
  ]);

  /* ------------------------------------------------------------------ */
  /*  CRITICAL FIX: use the SAME property name your auth context returns  */
  /*  (Original code used `isSuperAdminUser`; if your API changed it    */
  /*   to `isSuperAdmin`, keep the shorter name.)                        */
  /* ------------------------------------------------------------------ */
  const isSuperAdmin = user?.isSuperAdminUser ?? user?.isSuperAdmin ?? false;

  const { menus, rolePerms, isVisible } = useMemo(() => {
    switch (userRole) {
      case "admin":
        return {
          menus: adminMenus,
          rolePerms: permissions,
          isVisible: isSuperAdmin && permissions.length > 0,
        };
      case "client":
        return {
          menus: clientMenus,
          rolePerms: clientPermissions,
          isVisible: true,
        };
      case "subClient":
        return {
          menus: subClientMenus,
          rolePerms: subClientPermissions,
          isVisible: true,
        };
      default:
        return {
          menus: [] as MenuConfig[],
          rolePerms: [] as string[],
          isVisible: false,
        };
    }
  }, [
    userRole,
    adminMenus,
    clientMenus,
    subClientMenus,
    permissions,
    clientPermissions,
    subClientPermissions,
    isSuperAdmin,
  ]);

  return (
    <aside className={isSideBarOpen ? "sidebar" : "sidebar close"} id="sidebar">
      {isReloadSystemCacheLoading && <AppLoader />}

      <div className="sidebar__mobile__header">
        <div className="sidebar__mobile__brand">
          <Logo />
        </div>
        <button
          className="sidebar__close_btn"
          onClick={handleCloseSidebar}
          aria-label="Close sidebar"
          type="button"
        >
          <Icon name="back" size={20} />
        </button>
      </div>

      <nav className="menu" role="navigation" aria-label="Main Navigation">
        {isVisible &&
          menus.map((m) =>
            renderMenuItem(m, openMenu, handleMenuToggle, rolePerms),
          )}
      </nav>

      <div className="sidebar__version">
        <p>v({process.env.REACT_APP_VERSION})</p>
      </div>
    </aside>
  );
}